import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, TrendingUp, Zap, Wallet, Sparkles, Loader2, ArrowUpRight, CheckCircle2, XCircle, Clock, ShoppingBag, Smartphone } from "lucide-react";
import CashOutDialog from "@/components/CashOutDialog";
import { formatGyd } from "@/lib/currency";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function Earnings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [cashOutMethod, setCashOutMethod] = useState(null); // 'mmg' | 'shop' | null

  const load = () => {
    api
      .get("/me/earnings")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api
      .get("/me/payouts")
      .then((r) => setPayouts(r.data.payouts || []))
      .catch(() => setPayouts([]));
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    load();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-mono text-xs uppercase tracking-widest">
        sign in to see your earnings
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  const weekly = data.weekly || [];
  const maxWeekCoins = Math.max(1, ...weekly.map((w) => w.coins));

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
          // creator.earnings
        </p>
        <h1
          className="font-display font-black text-4xl md:text-5xl mb-1 glitch"
          data-text="EARNINGS"
        >
          EARNINGS
        </h1>
        <p className="text-white/40 text-sm font-mono">
          1000 coins = $10 · you receive 50% of every boost
        </p>

        {/* Coin balance hero */}
        <section
          data-testid="coin-balance-card"
          className="mt-8 border border-[#FF007F]/50 bg-gradient-to-br from-[#FF007F]/10 via-black to-transparent p-6 md:p-8 shadow-[0_0_40px_rgba(255,0,127,0.2)] relative overflow-hidden"
        >
          <div className="absolute top-4 right-4 opacity-20">
            <Coins className="h-24 w-24 text-[#FF007F]" strokeWidth={1} />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF007F]/80 flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5" /> current.balance
          </p>
          <p
            className="font-display font-black text-6xl md:text-7xl text-white mt-1 leading-none"
            data-testid="coin-balance-value"
          >
            {data.coins_balance.toLocaleString()}
            <span className="text-2xl md:text-3xl text-[#FF007F] font-bold ml-2">
              coins
            </span>
          </p>
          <p className="mt-2 font-mono text-sm text-white/60">
            ≈{" "}
            <span className="text-[#39FF14] font-bold text-lg">
              ${data.usd_balance.toFixed(2)}
            </span>{" "}
            USD
          </p>
          <p
            data-testid="coin-balance-gyd"
            className="font-mono text-xs text-[#00FFFF]/80"
          >
            ≈ <span className="font-bold">{formatGyd(data.usd_balance)}</span>{" "}
            <span className="text-white/40">// 1 USD = 200 GYD</span>
          </p>
          <button
            data-testid="open-cashout-mmg-btn"
            onClick={() => setCashOutMethod("mmg")}
            disabled={data.coins_balance < 5000}
            className="mt-5 relative z-10 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-sm px-5 py-3 hover:shadow-[0_0_25px_rgba(57,255,20,0.6)] transition-all disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2 mr-3"
          >
            <Smartphone className="h-4 w-4" strokeWidth={2.5} />
            {data.coins_balance >= 5000
              ? `MMG · get $${(data.coins_balance * (data.payout_net_ratios?.mmg ?? 0.5) / 100).toFixed(2)}`
              : `need $${(50 - data.coins_balance / 100).toFixed(2)} more`}
          </button>
          <button
            data-testid="open-cashout-shop-btn"
            onClick={() => setCashOutMethod("shop")}
            disabled={data.coins_balance < (data.payout_min_coins_per_method?.shop ?? 100)}
            className="mt-5 relative z-10 border border-[#00FFFF]/60 bg-[#00FFFF]/5 text-[#00FFFF] font-bold uppercase tracking-widest text-sm px-5 py-3 hover:bg-[#00FFFF]/10 hover:shadow-[0_0_25px_rgba(0,255,255,0.4)] transition-all disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <ShoppingBag className="h-4 w-4" strokeWidth={2.5} />
            {data.coins_balance >= (data.payout_min_coins_per_method?.shop ?? 100)
              ? `SHOP · get $${(data.coins_balance / 100).toFixed(2)}`
              : `need $${(1 - data.coins_balance / 100).toFixed(2)} more`}
          </button>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/30">
            // MMG min $50 · shop min $1 · custom amount at next step
          </p>
        </section>

        {/* Stats grid */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <div
            data-testid="stat-lifetime"
            className="p-4 border border-white/10 bg-[#0A0A0C]"
          >
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> lifetime
            </p>
            <p className="font-display font-black text-2xl text-white mt-1">
              {data.coins_lifetime.toLocaleString()}
            </p>
            <p className="font-mono text-xs text-[#39FF14]">
              ${data.usd_lifetime.toFixed(2)}
            </p>
            <p className="font-mono text-[10px] text-[#00FFFF]/70" data-testid="lifetime-gyd">
              {formatGyd(data.usd_lifetime)}
            </p>
            {data.coins_lifetime > data.coins_balance && (
              <p
                data-testid="stat-lifetime-spent"
                className="font-mono text-[10px] text-white/40 mt-2 leading-snug"
              >
                already spent / paid out:{" "}
                <span className="text-[#FF007F]">
                  {(data.coins_lifetime - data.coins_balance).toLocaleString()}
                </span>{" "}
                (${((data.coins_lifetime - data.coins_balance) / 100).toFixed(2)})
              </p>
            )}
          </div>
          <div
            data-testid="stat-this-week"
            className="p-4 border border-[#39FF14]/40 bg-[#39FF14]/5 shadow-[0_0_14px_rgba(57,255,20,0.12)]"
          >
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> this.week
            </p>
            <p className="font-display font-black text-2xl text-white mt-1">
              {data.this_week.coins.toLocaleString()}
            </p>
            <p className="font-mono text-xs text-[#39FF14]">
              ${data.this_week.usd.toFixed(2)}
            </p>
          </div>
          <div className="p-4 border border-white/10 bg-[#0A0A0C]">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              boosts.this.week
            </p>
            <p className="font-display font-black text-2xl text-white mt-1">
              {data.this_week.count}
            </p>
            <p className="font-mono text-xs text-white/40">supporters</p>
          </div>
        </section>

        {/* Weekly bar graph */}
        {weekly.length > 0 && (
          <section className="mt-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
              // last.8.weeks
            </p>
            <div
              data-testid="weekly-graph"
              className="flex items-end gap-2 h-32 border-b border-white/10 pb-1"
            >
              {weekly.map((w) => {
                const pct = (w.coins / maxWeekCoins) * 100;
                return (
                  <div
                    key={w.week}
                    className="flex-1 min-w-0 flex flex-col justify-end items-center gap-1"
                    title={`${w.week}: ${w.coins} coins ($${(w.coins / 100).toFixed(2)}) · ${w.count} boosts`}
                  >
                    <span className="font-mono text-[9px] text-white/40">
                      {w.coins}
                    </span>
                    <div
                      className="w-full bg-gradient-to-t from-[#FF007F] to-[#00FFFF] transition-all"
                      style={{ height: `${Math.max(pct, 3)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-1">
              {weekly.map((w) => (
                <span
                  key={`${w.week}-lbl`}
                  className="flex-1 font-mono text-[9px] uppercase text-white/30 text-center truncate"
                >
                  {w.week.split("-W")[1] ? `w${w.week.split("-W")[1]}` : w.week}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Recent tips */}
        <section className="mt-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
            // recent.boosts
          </p>
          {data.recent_tips.length === 0 ? (
            <div
              data-testid="earnings-empty"
              className="py-10 text-center font-mono text-xs uppercase tracking-widest text-white/30"
            >
              // no boosts yet — share your videos to earn
            </div>
          ) : (
            <ul data-testid="recent-tips" className="space-y-2">
              {data.recent_tips.map((t) => (
                <li
                  key={t.id || t.stripe_session_id}
                  data-testid={`earnings-tip-${t.id || t.stripe_session_id}`}
                  className="flex items-center gap-3 p-3 border border-white/10 bg-[#0A0A0C] hover:border-[#FF007F]/40 transition-colors"
                >
                  {t.tipper_picture ? (
                    <img
                      src={picUrl(t.tipper_picture)}
                      alt=""
                      className="h-9 w-9 object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-9 w-9 flex items-center justify-center bg-[#FF007F]/20 border border-[#FF007F]/40 text-[#FF007F] text-sm font-bold flex-shrink-0">
                      {(t.tipper_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      <span className="font-semibold">
                        {t.tipper_name || "Anonymous"}
                      </span>{" "}
                      <span className="text-white/40">boosted</span>{" "}
                      {t.video_id && (
                        <Link
                          to={`/v/${t.video_id}`}
                          className="text-[#00FFFF] hover:text-[#39FF14]"
                        >
                          your video
                        </Link>
                      )}
                    </p>
                    {t.message && (
                      <p className="text-xs text-white/50 italic truncate mt-0.5">
                        “{t.message}”
                      </p>
                    )}
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-1">
                      {timeAgo(t.created_at)} ago
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-display font-black text-[#FF007F] text-lg flex items-center gap-1 justify-end">
                      <Zap className="h-3.5 w-3.5" fill="#FF007F" strokeWidth={0} />+
                      {(t.creator_coins ?? Math.floor((t.amount_cents || 0) / 2)).toLocaleString()}
                    </p>
                    <p className="font-mono text-[9px] text-white/40">
                      of {t.amount_cents}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="mt-10 mb-6 font-mono text-[10px] uppercase tracking-widest text-white/25 text-center">
          // manual review · MMG min $50 · shop credit any amount · 3 biz days
        </p>

        {payouts.length > 0 && (
          <section className="mt-6 mb-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
              // payout.history
            </p>
            <ul data-testid="payout-history" className="space-y-2">
              {payouts.map((p) => {
                const Icon =
                  p.status === "paid" ? CheckCircle2 : p.status === "rejected" ? XCircle : Clock;
                const color =
                  p.status === "paid"
                    ? "text-[#39FF14] border-[#39FF14]/40"
                    : p.status === "rejected"
                    ? "text-white/40 border-white/10"
                    : "text-[#00FFFF] border-[#00FFFF]/40";
                return (
                  <li
                    key={p.id}
                    data-testid={`payout-${p.id}`}
                    className={`flex items-center gap-3 p-3 border bg-[#0A0A0C] ${color}`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.8} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white">
                        <span className="font-display font-black text-[#39FF14]">
                          ${((p.net_amount_cents ?? p.amount_cents) / 100).toFixed(2)}
                        </span>{" "}
                        <span className="text-white/60">via</span>{" "}
                        <span className="font-mono text-xs uppercase text-[#00FFFF]">
                          {p.method === "shop" ? "shop.matrix" : (p.method || "mmg")}
                        </span>{" "}
                        <span className="font-mono text-xs text-white/50 truncate">
                          · {p.mmg_phone || p.shop_phone || p.shop_handle || p.paypal_email || "—"}
                        </span>
                        {p.net_amount_cents != null && p.net_amount_cents !== p.amount_cents && (
                          <span className="ml-2 font-mono text-[10px] text-white/40">
                            (from ${(p.amount_cents / 100).toFixed(2)})
                          </span>
                        )}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-0.5">
                        {p.status} · {new Date(p.created_at).toLocaleDateString()}
                        {p.paid_at && ` · paid ${new Date(p.paid_at).toLocaleDateString()}`}
                      </p>
                      {p.admin_note && (
                        <p className="text-xs text-white/50 mt-1 italic">
                          note: {p.admin_note}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      <CashOutDialog
        open={cashOutMethod !== null}
        method={cashOutMethod || "mmg"}
        onClose={() => setCashOutMethod(null)}
        balance={data.coins_balance}
        netRatios={data.payout_net_ratios}
        minPerMethod={data.payout_min_coins_per_method}
        defaultShopHandle={user?.username || ""}
        onSuccess={() => load()}
      />
    </div>
  );
}
